import { useState, useEffect, useRef } from "react";
import {
  getCompaniesApi,
  getCompanyByIdApi,
  createCompanyApi,
  updateCompanyApi,
  deleteCompanyApi,
  getCompanyTypesApi,
} from "../../services/company/companyService";
import { getMenusApi } from "../../services/menu/menuService";
import toast from "react-hot-toast";

export const useCompanyViewModel = () => {
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);

  const [companyTypes, setCompanyTypes] = useState<any[]>([]);
  const [menus, setMenus] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useState("");

  const [typePage, setTypePage] = useState(1);
  const [typeSearch, setTypeSearch] = useState("");

  // 🔥 GUARDS TO PREVENT DOUBLE CALL
  const companiesLoaded = useRef(false);
  const typesLoaded = useRef(false);
  const menusLoaded = useRef(false);

  // ================= LOAD COMPANIES =================
  const loadCompanies = async () => {
    setLoading(true);
    try {
      const res = await getCompaniesApi(search, pageNumber, pageSize);

      setCompanies(res.data || []);
      setTotalCount(res.totalCount || 0);
    } catch (err: any) {
      toast.error(err.message || "Failed to load companies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // allow first load once, then allow normal updates
    if (!companiesLoaded.current) {
      companiesLoaded.current = true;
      loadCompanies();
      return;
    }

    loadCompanies();
  }, [pageNumber, search]);

  // ================= LOAD COMPANY TYPES =================
  const loadCompanyTypes = async () => {
    try {
      const res = await getCompanyTypesApi(typePage, 10, typeSearch);
      setCompanyTypes(res?.data || []);
    } catch {
      toast.error("Failed to load company types");
    }
  };

  useEffect(() => {
    if (!typesLoaded.current) {
      typesLoaded.current = true;
      loadCompanyTypes();
      return;
    }

    loadCompanyTypes();
  }, [typePage, typeSearch]);

  // ================= LOAD MENUS =================
  const loadMenus = async () => {
    try {
      const res = await getMenusApi();
      setMenus(res || []);
    } catch {
      toast.error("Failed to load menus");
    }
  };

  useEffect(() => {
    if (menusLoaded.current) return;

    menusLoaded.current = true;
    loadMenus();
  }, []);

  // ================= CREATE =================
  const createCompany = async (data: any) => {
    try {
      await createCompanyApi(data);
      toast.success("Company created");

      await loadCompanies();
    } catch (err: any) {
      toast.error(err.message || "Create failed");
      throw err;
    }
  };

  // ================= GET BY ID =================
  const getCompanyById = async (id: string) => {
    setLoading(true);
    try {
      const res = await getCompanyByIdApi(id);

      setSelectedCompany(res);
      return res;
    } catch (err: any) {
      toast.error(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // ================= UPDATE =================
  const updateCompany = async (data: any) => {
    try {
      const res = await updateCompanyApi(data);

      toast.success("Company updated");

      await loadCompanies();

      return res;
    } catch (err: any) {
      toast.error(err.message);
      throw err;
    }
  };

  // ================= DELETE =================
  const deleteCompany = async (id: string) => {
    try {
      await deleteCompanyApi(id);

      toast.success("Company deleted");

      await loadCompanies();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  return {
    companies,
    selectedCompany,
    companyTypes,
    menus,
    loading,

    pageNumber,
    totalCount,
    setPageNumber,

    search,
    setSearch,

    typeSearch,
    setTypeSearch,
    typePage,
    setTypePage,

    createCompany,
    updateCompany,
    deleteCompany,
    getCompanyById,

    reload: loadCompanies,
  };
};